
import throttle from 'lodash/throttle';
import cloneDeep from 'lodash/cloneDeep';
import noop from 'lodash/noop';

export class ThrottleData {
  pool = new Set();
  running = false;

  constructor(processFn, options) {
    const { wait = 1000, clone = false } = options || {};
    if (options) {
      Reflect.deleteProperty(options, 'wait');
      Reflect.deleteProperty(options, 'clone');
    }
    this.clone = clone;
    this.processFn = processFn;
    this.throttleProcess = throttle(this.process, wait, options);
  }

  put(data) {
    this.pool.add(data);
    this.throttleProcess();
    return this;
  }

  putAll(array) {
    array.forEach(item => this.pool.add(item));
    this.throttleProcess();
    return this;
  }

  async process() {
    if (this.running) {
      return;
    }
    this.running = true;
    const pool = [...this.pool];
    this.pool.clear();
    try {
      await this.processFn(this.clone ? cloneDeep(pool) : pool);
    } finally {
      this.running = false;
      if (this.pool.size > 0) {
        this.throttleProcess();
      }
    }
  }

  clear() {
    this.pool.clear();
    return this;
  }

  destroy() {
    this.clear();
    this.processFn = noop;
    this.throttleProcess && this.throttleProcess.cancel && this.throttleProcess.cancel();
    this.throttleProcess = noop;
  }
}

/**
 * 超时执行
 * @param fn 要执行的函数
 * @param time 超时时间
 * @param error 超时错误信息
 * @returns {Promise<unknown>}
 */
export function timeoutExecute(fn, time, error = 'timeout') {
  let timer;
  const fnPromise = typeof fn === 'function' ? fn() : fn;
  const cancel = () => timer && clearTimeout(timer);
  const promise = Promise.race([
    new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(error), time);
    }),
    fnPromise
  ]).finally(cancel);
  promise.cancel = cancel;
  return promise;
}

const run = Symbol();
export class QueueTimeoutError extends Error {
  job;
  constructor(fn, msg) {
    super(msg);
    this.job = fn;
  }
}

export class QueueJob {
  jobs = [];
  executing = false;
  timeout = 0;
  onIdle = () => {};
  onError = e => console.log('队列函数执行异常', e.stack || e.message);

  /**
   * 构造函数
   * @param onError 错误处理函数
   * @param onIdle 空闲回调函数
   * @param timeout 超时时间（毫秒，需要大于0）
   */
  constructor({ onError, onIdle, timeout } = {}) {
    if (typeof onError === 'function') {
      this.onError = onError;
    }
    if (typeof onIdle === 'function') {
      this.onIdle = onIdle;
    }
    if (typeof timeout === 'number') {
      this.timeout = timeout;
    }
  }

  /**
   * 添加队列
   * @param fn
   */
  push(fn) {
    this.jobs.push(fn);
    this[run]();
    return this;
  }

  /**
   * 错误恢复执行队列
   */
  resume() {
    this.executing = false;
    this[run]();
    return this;
  }

  async [run]() {
    if (this.executing) {
      return;
    }
    if (!this.jobs.length) {
      this.onIdle();
      return;
    }
    this.executing = true;
    const fn = this.jobs.shift();
    try {
      if (this.timeout > 0) {
        this.currentExcute = timeoutExecute(fn, this.timeout, new QueueTimeoutError(fn, 'timeout'));
        await this.currentExcute;
      } else {
        await fn();
      }
    } catch (e) {
      await this.onError(e);
    } finally {
      this.currentExcute = null;
    }
    this.executing = false;
    this[run]();
  }

  destroy() {
    if (this.currentExcute?.cancel) {
      this.currentExcute.cancel();
    }
    this.executing = true;
    this.jobs.length = 0;
    this.jobs = [];
  }
}

